// Marketplace data managed via TanStack Query
// Provides query options and hooks for fetching marketplace campaigns

import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { trpcClient } from "../trpc";

// ============================================================================
// Query Keys
// ============================================================================

export const marketplaceKeys = {
  all: ["marketplace"] as const,
  campaignsByOrg: (organizationId: string) =>
    [...marketplaceKeys.all, "campaigns", organizationId] as const,
  ownProgramIdsByOrg: (organizationId: string) =>
    [...marketplaceKeys.all, "ownProgramIds", organizationId] as const,
};

// ============================================================================
// Types - Inferred from API response
// ============================================================================

type MarketplaceCampaignsResult = Awaited<
  ReturnType<typeof trpcClient.marketplace.campaigns.query>
>;

export type MarketplaceCampaign =
  MarketplaceCampaignsResult["campaigns"][number];

// ============================================================================
// Types - Filter Options
// ============================================================================

export interface MarketplaceFilters {
  networks?: string[];
  categories?: string[];
  compensations?: string[];
  keywords?: string[];
}

// ============================================================================
// Constants
// ============================================================================

export const MARKETPLACE_PAGE_SIZE = 24;

/**
 * Last page the ES-backed search (v2) serves: backend_server clamps any higher
 * page to 99 and returns it again, so hasMore alone would never turn false.
 */
export const MARKETPLACE_V2_MAX_PAGE = 99;

// ============================================================================
// Query Options
// ============================================================================

/**
 * Infinite query options for fetching marketplace campaigns by organization ID.
 * Loads campaigns in pages of 24 for infinite scroll.
 */
export function marketplaceCampaignsQueryOptions(
  organizationId: string,
  filters?: MarketplaceFilters,
  enabled = true,
) {
  return infiniteQueryOptions({
    queryKey: [...marketplaceKeys.campaignsByOrg(organizationId), filters],
    queryFn: ({ pageParam }) =>
      trpcClient.marketplace.campaigns.query({
        organizationId,
        limit: MARKETPLACE_PAGE_SIZE,
        page: pageParam,
        ...filters,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.page + 1 : undefined,
    enabled: enabled && organizationId.length > 0,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook for fetching marketplace campaigns with infinite scroll.
 */
export function useMarketplaceCampaignsQuery(
  organizationId: string,
  filters?: MarketplaceFilters,
  enabled = true,
) {
  return useInfiniteQuery(
    marketplaceCampaignsQueryOptions(organizationId, filters, enabled),
  );
}

// ============================================================================
// V2 - ES-backed search
// ============================================================================

/**
 * Infinite query options for fetching marketplace campaigns via ES search (v2).
 */
export function marketplaceCampaignsV2QueryOptions(
  organizationId: string,
  filters?: MarketplaceFilters,
  enabled = true,
) {
  return infiniteQueryOptions({
    queryKey: [
      ...marketplaceKeys.campaignsByOrg(organizationId),
      "v2",
      filters,
    ],
    queryFn: ({ pageParam }) =>
      trpcClient.marketplace.campaignsV2.query({
        organizationId,
        limit: MARKETPLACE_PAGE_SIZE,
        page: pageParam,
        ...filters,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore &&
      lastPage.pagination.page < MARKETPLACE_V2_MAX_PAGE
        ? lastPage.pagination.page + 1
        : undefined,
    enabled: enabled && organizationId.length > 0,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Hook for fetching marketplace campaigns via ES search (v2) with infinite scroll.
 */
export function useMarketplaceCampaignsV2Query(
  organizationId: string,
  filters?: MarketplaceFilters,
  enabled = true,
) {
  return useInfiniteQuery(
    marketplaceCampaignsV2QueryOptions(organizationId, filters, enabled),
  );
}

// ============================================================================
// The creator's own programs (EPD-12266)
// ============================================================================

/**
 * Query options for the programs the org's creators applied to and the
 * programs a brand added them to directly. Fetched once per Marketplace
 * visit and checked client-side: applied ones get the "Applied" badge and
 * are hidden by default, directly added ones are always hidden.
 */
export function marketplaceOwnProgramIdsQueryOptions(organizationId: string) {
  return queryOptions({
    queryKey: marketplaceKeys.ownProgramIdsByOrg(organizationId),
    queryFn: () =>
      trpcClient.marketplace.ownProgramIds.query({ organizationId }),
    enabled: organizationId.length > 0,
    // The server never throws for AspireX failures, so an error here is a
    // transport failure. The grid waits for this lookup, so fail fast to
    // "nothing hidden, no badges" rather than retry for ~7s.
    retry: false,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Hook for the programs the org's creators applied to or were added to directly.
 */
export function useMarketplaceOwnProgramIdsQuery(organizationId: string) {
  return useQuery(marketplaceOwnProgramIdsQueryOptions(organizationId));
}
